<script setup lang="ts">
/**
 * A template's mark on a tinted plate — the tile every image picker shows.
 *
 * Iconify ships the SHAPE of a brand mark, not its colour, so 105 apps drawn in
 * one ink read as a wall of identical grey squares, which is what the
 * marketplace was. The mark is painted in its brand colour (`BRAND_COLORS`, the
 * same `simple-icons` data the website inlines), a pictogram in its shelf's
 * accent, and each colour is moved toward contrast per theme by `brandTones`
 * (black marks lift on a dark card, pale ones darken on a light card). A
 * multi-colour `i-logos-*` mark and an image keep their own colours on a
 * neutral plate.
 */
const props = withDefaults(defineProps<{
  iconUrl?: string | null
  /** The name-matching fallback (`fallbackIcon`). */
  fallback: string
  /** Colour for a pictogram: the app's shelf accent. */
  accent?: string | null
  size?: 'sm' | 'md' | 'lg'
}>(), {
  iconUrl: null,
  accent: null,
  size: 'md',
})

const resolved = computed(() => resolveTemplateIcon(props.iconUrl, props.fallback))

const tones = computed(() => {
  if (resolved.value.kind !== 'icon') return null
  const color = iconColor(resolved.value.value, props.accent)
  return color ? brandTones(color) : null
})

const sizing = computed(() => ({
  sm: { plate: 'size-8 rounded-lg', icon: 'size-4.5' },
  md: { plate: 'size-11 rounded-xl', icon: 'size-6' },
  lg: { plate: 'size-12 rounded-xl', icon: 'size-7' },
}[props.size]))
</script>

<template>
  <!-- Classes written out in full: Tailwind generates utilities by scanning
       source text, so an interpolated colour would compile to nothing. -->
  <span
    v-if="tones"
    :class="[
      'flex shrink-0 items-center justify-center text-(--logo-light) dark:text-(--logo-dark)',
      'bg-[color-mix(in_oklab,var(--logo-light)_11%,transparent)] dark:bg-[color-mix(in_oklab,var(--logo-dark)_18%,transparent)]',
      'ring-1 ring-inset ring-[color-mix(in_oklab,var(--logo-light)_18%,transparent)] dark:ring-[color-mix(in_oklab,var(--logo-dark)_24%,transparent)]',
      sizing.plate,
    ]"
    :style="{ '--logo-light': tones.light, '--logo-dark': tones.dark }"
    aria-hidden="true"
  >
    <TemplateIcon :icon-url="iconUrl" :fallback="fallback" :class="sizing.icon" />
  </span>
  <span
    v-else
    :class="[
      'flex shrink-0 items-center justify-center bg-elevated ring-1 ring-inset ring-default',
      sizing.plate,
    ]"
    aria-hidden="true"
  >
    <TemplateIcon :icon-url="iconUrl" :fallback="fallback" :class="sizing.icon" />
  </span>
</template>
